// built-from: /attachment-validation-routing v1 impl:attachment-validation-routing role:implementation
// Client-side attachment validation. Mirrors the server's accept rules in
// functions/_attachments.js so users see a clear error BEFORE the upload
// round-trips and gets rejected with a 400. If you change the server-side
// accepted set, update this file too.

export const ACCEPTED_FILE_ACCEPT_ATTR =
  'image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,text/markdown,text/csv,application/json,.md,.txt,.csv,.json';

const IMAGE_MIMES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
const PDF_MIMES = ['application/pdf'];
const TEXT_MIMES = [
  'text/plain',
  'text/markdown',
  'text/x-markdown',
  'text/csv',
  'application/json',
  'text/html',
  'text/xml',
  'application/xml',
];
const DOCX_MIMES = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
];

const TEXT_EXTENSIONS = ['.txt', '.md', '.markdown', '.csv', '.json', '.html', '.htm', '.xml'];
const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp'];
const PDF_EXTENSIONS = ['.pdf'];
const DOCX_EXTENSIONS = ['.docx', '.doc'];

function extensionOf(filename: string): string {
  const idx = filename.lastIndexOf('.');
  return idx === -1 ? '' : filename.slice(idx).toLowerCase();
}

export type AttachmentValidationResult =
  | { ok: true }
  | { ok: false; reason: string };

export function validateAttachment(file: File): AttachmentValidationResult {
  const mime = (file.type || '').toLowerCase();
  const ext = extensionOf(file.name || '');

  // DOCX gets the same actionable message the server would return. 093: it
  // says what to do and names no vendor.
  if (DOCX_MIMES.includes(mime) || DOCX_EXTENSIONS.includes(ext)) {
    return {
      ok: false,
      reason: `"${file.name}" is a Word document, which cannot be read here. Save it as PDF and add that instead.`,
    };
  }

  if (
    IMAGE_MIMES.includes(mime) ||
    IMAGE_EXTENSIONS.includes(ext) ||
    mime.startsWith('image/')
  ) {
    return { ok: true };
  }
  if (PDF_MIMES.includes(mime) || PDF_EXTENSIONS.includes(ext)) {
    return { ok: true };
  }
  if (
    TEXT_MIMES.includes(mime) ||
    TEXT_EXTENSIONS.includes(ext) ||
    mime.startsWith('text/')
  ) {
    return { ok: true };
  }

  const label = ext || mime || 'unknown type';
  return {
    ok: false,
    reason: `"${file.name}" (${label}) isn't a supported attachment. Supported: images (PNG/JPG/GIF/WebP), PDF, plain text, markdown, CSV, JSON.`,
  };
}

// Validate a batch. Returns the accepted files and any error reasons.
export function validateAttachmentBatch(files: File[] | FileList): {
  accepted: File[];
  errors: string[];
} {
  const list = Array.from(files);
  const accepted: File[] = [];
  const errors: string[] = [];
  for (const f of list) {
    const result = validateAttachment(f);
    if (result.ok) accepted.push(f);
    else errors.push(result.reason);
  }
  return { accepted, errors };
}
